import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Hotel, MapPin, Calendar, Users, Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import HotelSearchBar from "../../components/HotelSearchBar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_availability: { label: "Pending Availability", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  available: { label: "Available — Confirm Booking", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  not_available: { label: "Not Available", color: "bg-rose-100 text-rose-700 border-rose-200", icon: XCircle },
  guest_confirmed: { label: "Guest Confirmed — Awaiting Hotel", color: "bg-violet-100 text-violet-700 border-violet-200", icon: AlertCircle },
  hotel_confirmed: { label: "Hotel Confirmed", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-600 border-gray-200", icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending_availability;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

const initialForm = {
  hotel_name: "", city: "", destination: "",
  check_in: "", check_out: "", rooms: 1,
  room_type: "Deluxe", meal_plan: "CP",
  pax_adults: 2, pax_children: 0, cwb: 0, cnb: 0,
  special_requests: "",
};

export default function AgentHotels() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("all");

  const fetchRows = async () => {
    try {
      const res = await axios.get(`${API}/hotel-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(res.data || []);
    } catch (e) {
      toast.error("Failed to load hotel requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick-search submit: creates a hotel request directly (skips the detailed modal).
  // Admin will set prices in their "Mark Available" step as before.
  const handleSearchSubmit = async (payload) => {
    try {
      const res = await axios.post(`${API}/hotel-requests`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Check-availability request sent — admin will respond shortly");
      navigate(`/agent/hotels/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    }
  };

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "active") return rows.filter(r => ["pending_availability", "available", "guest_confirmed"].includes(r.status));
    if (tab === "confirmed") return rows.filter(r => r.status === "hotel_confirmed");
    if (tab === "completed") return rows.filter(r => r.status === "completed");
    if (tab === "closed") return rows.filter(r => ["not_available", "cancelled"].includes(r.status));
    return rows;
  }, [rows, tab]);

  const submitForm = async () => {
    // Hotel name & room type are optional — agent may not know the hotel yet,
    // admin will fill / override at the "mark available" step.
    if (!form.city.trim() || !form.check_in || !form.check_out) {
      toast.error("City, check-in and check-out are required");
      return;
    }
    if (new Date(form.check_out) <= new Date(form.check_in)) {
      toast.error("Check-out must be after check-in");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/hotel-requests`, {
        ...form,
        rooms: parseInt(form.rooms || 1, 10),
        pax_adults: parseInt(form.pax_adults || 1, 10),
        pax_children: parseInt(form.pax_children || 0, 10),
        cwb: parseInt(form.cwb || 0, 10),
        cnb: parseInt(form.cnb || 0, 10),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hotel request submitted — admin will check availability shortly");
      setShowCreate(false);
      setForm(initialForm);
      navigate(`/agent/hotels/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "all", label: "All", count: rows.length },
    { key: "active", label: "Active", count: rows.filter(r => ["pending_availability", "available", "guest_confirmed"].includes(r.status)).length },
    { key: "confirmed", label: "Confirmed", count: rows.filter(r => r.status === "hotel_confirmed").length },
    { key: "completed", label: "Completed", count: rows.filter(r => r.status === "completed").length },
    { key: "closed", label: "Closed", count: rows.filter(r => ["not_available", "cancelled"].includes(r.status)).length },
  ];

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Hotel className="w-7 h-7 text-emerald-600" /> Hotel Requests</h1>
            <p className="text-gray-500 text-sm">Quick check-availability — admin will respond with prices</p>
          </div>
          <Button onClick={() => setShowCreate(true)} variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="agent-new-hotel-request-btn">
            <Plus className="w-4 h-4" /> Detailed Request (with hotel name, room type…)
          </Button>
        </div>

        {/* Quick search-bar (MakeMyTrip-style) */}
        <HotelSearchBar onSubmit={handleSearchSubmit} />

        <div className="flex flex-wrap gap-2">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${tab === t.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"}`}
              data-testid={`agent-hotels-tab-${t.key}`}
            >
              {t.label} <span className="ml-1 text-xs opacity-80">({t.count})</span>
            </button>
          ))}
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Hotel className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No hotel requests yet.</p>
                <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-4">Create your first request</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-3">Request</th>
                      <th className="px-4 py-3">Hotel</th>
                      <th className="px-4 py-3">City</th>
                      <th className="px-4 py-3">Dates</th>
                      <th className="px-4 py-3 text-right">Pax / Rooms</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr
                        key={r.id}
                        className="border-b border-gray-100 hover:bg-emerald-50/30 cursor-pointer"
                        onClick={() => navigate(`/agent/hotels/${r.id}`)}
                        data-testid={`agent-hotel-row-${r.id}`}
                      >
                        <td className="px-4 py-3 font-mono text-emerald-700 text-xs font-semibold">{r.request_number}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{r.hotel_name}</td>
                        <td className="px-4 py-3 text-gray-700">{r.city}</td>
                        <td className="px-4 py-3 text-gray-700">{r.check_in} → {r.check_out} <span className="text-xs text-gray-500">({r.nights}N)</span></td>
                        <td className="px-4 py-3 text-right text-gray-700">{r.pax_adults}A{r.pax_children > 0 ? ` + ${r.pax_children}C` : ""} / {r.rooms}R</td>
                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>New Hotel Availability Request</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-sm">Hotel Name <span className="text-gray-400 text-xs">(optional — if known)</span></Label>
              <Input value={form.hotel_name} onChange={e => setForm({ ...form, hotel_name: e.target.value })} placeholder="e.g. Taj Bengal (leave blank if not decided)" data-testid="hotel-form-name" />
            </div>
            <div>
              <Label className="text-sm">City *</Label>
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Kolkata" data-testid="hotel-form-city" />
            </div>
            <div>
              <Label className="text-sm">Destination / State</Label>
              <Input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder="West Bengal" data-testid="hotel-form-destination" />
            </div>
            <div>
              <Label className="text-sm">Check-in *</Label>
              <Input type="date" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} data-testid="hotel-form-checkin" />
            </div>
            <div>
              <Label className="text-sm">Check-out *</Label>
              <Input type="date" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} data-testid="hotel-form-checkout" />
            </div>
            {(() => {
              if (!form.check_in || !form.check_out) return null;
              const ci = new Date(form.check_in);
              const co = new Date(form.check_out);
              const ms = co - ci;
              if (isNaN(ms)) return null;
              const nights = Math.round(ms / (1000 * 60 * 60 * 24));
              if (nights <= 0) {
                return (
                  <div className="sm:col-span-2 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="nights-error">
                    Check-out must be after check-in
                  </div>
                );
              }
              return (
                <div className="sm:col-span-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center gap-2" data-testid="nights-preview">
                  <span className="text-xs uppercase tracking-wide font-semibold opacity-80">Total Nights</span>
                  <span className="font-bold text-base">{nights}N</span>
                  <span className="text-xs text-emerald-700">({nights + 1} day{nights ? "s" : ""})</span>
                </div>
              );
            })()}
            <div>
              <Label className="text-sm">Rooms</Label>
              <Input type="number" min="1" value={form.rooms} onChange={e => setForm({ ...form, rooms: e.target.value })} data-testid="hotel-form-rooms" />
            </div>
            <div>
              <Label className="text-sm">Room Type</Label>
              <Input value={form.room_type} onChange={e => setForm({ ...form, room_type: e.target.value })} placeholder="Deluxe / Suite / Standard" data-testid="hotel-form-room-type" />
            </div>
            <div>
              <Label className="text-sm">Meal Plan</Label>
              <Select value={form.meal_plan} onValueChange={v => setForm({ ...form, meal_plan: v })}>
                <SelectTrigger data-testid="hotel-form-meal-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CP">CP (Breakfast)</SelectItem>
                  <SelectItem value="MAP">MAP (B + Dinner)</SelectItem>
                  <SelectItem value="AP">AP (All meals)</SelectItem>
                  <SelectItem value="EP">EP (Room only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Adults</Label>
              <Input type="number" min="1" value={form.pax_adults} onChange={e => setForm({ ...form, pax_adults: e.target.value })} data-testid="hotel-form-adults" />
            </div>
            <div>
              <Label className="text-sm">Children</Label>
              <Input type="number" min="0" value={form.pax_children} onChange={e => setForm({ ...form, pax_children: e.target.value })} data-testid="hotel-form-children" />
            </div>
            <div>
              <Label className="text-sm">CWB (Child with bed)</Label>
              <Input type="number" min="0" value={form.cwb} onChange={e => setForm({ ...form, cwb: e.target.value })} data-testid="hotel-form-cwb" />
            </div>
            <div>
              <Label className="text-sm">CNB (Child no bed)</Label>
              <Input type="number" min="0" value={form.cnb} onChange={e => setForm({ ...form, cnb: e.target.value })} data-testid="hotel-form-cnb" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Special Requests</Label>
              <textarea
                value={form.special_requests}
                onChange={e => setForm({ ...form, special_requests: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                placeholder="High floor, early check-in, late check-out, etc."
                data-testid="hotel-form-special"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
            <Button onClick={submitForm} disabled={submitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="hotel-form-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : "Submit Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
